import { FC, ReactNode, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

type NavItem = {
  href: string;
  label: string;
  icon: ReactNode;
};

const navItems: NavItem[] = [
  {
    href: '/player',
    label: 'Player',
    icon: <path d="M9 18V5l10-2v13M9 9l10-2M6.5 21A2.5 2.5 0 1 0 6.5 16a2.5 2.5 0 0 0 0 5Zm10-2A2.5 2.5 0 1 0 16.5 14a2.5 2.5 0 0 0 0 5Z" />,
  },
  {
    href: '/history',
    label: 'History',
    icon: <path d="M12 8v4l2.5 1.5M4.9 5.3A8 8 0 1 1 4 16M4 5v4h4" />,
  },
  {
    href: '/library',
    label: 'Library',
    icon: <path d="M5 4h14v16H5zM9 4v16M13 8h3M13 12h3" />,
  },
  {
    href: '/explore',
    label: 'Explore',
    icon: <path d="m14.8 9.2-2 5.6-5.6 2 2-5.6 5.6-2ZM12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z" />,
  },
];

const moreItems = [
  { href: '/stats', label: 'Long-term Stats' },
  { href: '/tags', label: 'Settings' },
];

const Menu: FC = () => {
  const router = useRouter();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreActive = moreItems.some(item => router.pathname === item.href);

  useEffect(() => setMoreOpen(false), [router.asPath]);

  const isActive = (path: string): boolean => {
    if (path === '/history' && router.pathname === '/') return true;
    return router.pathname === path;
  };

  return (
    <>
      <nav className="desktop-nav" aria-label="Primary navigation">
        <Link href="/player" className="brand-link" aria-label="Music Man home">
          <span className="brand-mark" aria-hidden="true">●</span>
          <span>Music Man</span>
        </Link>
        <div className="desktop-nav-links">
          {navItems.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              aria-current={isActive(item.href) ? 'page' : undefined}
              className={`desktop-nav-link ${isActive(item.href) ? 'is-active' : ''}`}
            >
              {item.label}
            </Link>
          ))}
          <div className="more-menu-wrapper">
            <button onClick={() => setMoreOpen(open => !open)} aria-label="More" aria-expanded={moreOpen} className={`desktop-nav-link ${moreActive ? 'is-active' : ''}`}>···</button>
            {moreOpen && <div className="more-menu desktop-more-menu">{moreItems.map(item => <Link key={item.href} href={item.href} className={isActive(item.href) ? 'is-active' : ''}>{item.label}</Link>)}</div>}
          </div>
        </div>
      </nav>

      <nav className="mobile-nav" aria-label="Primary navigation">
        {navItems.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            aria-current={isActive(item.href) ? 'page' : undefined}
            className={`mobile-nav-link ${isActive(item.href) ? 'is-active' : ''}`}
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              {item.icon}
            </svg>
            <span>{item.label}</span>
          </Link>
        ))}
        <div className="more-menu-wrapper mobile-more-wrapper">
          <button onClick={() => setMoreOpen(open => !open)} aria-label="More" aria-expanded={moreOpen} className={`mobile-nav-link w-full ${moreActive ? 'is-active' : ''}`}>
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.7"/><circle cx="12" cy="12" r="1.7"/><circle cx="19" cy="12" r="1.7"/></svg>
            <span>···</span>
          </button>
          {moreOpen && <div className="more-menu mobile-more-menu">{moreItems.map(item => <Link key={item.href} href={item.href} className={isActive(item.href) ? 'is-active' : ''}>{item.label}</Link>)}</div>}
        </div>
      </nav>
    </>
  );
};

export default Menu;
